import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import './MetaphorNetwork.css';

const COLORS = {
  'extension-of-self': '#667eea',
  'magical-tool': '#764ba2',
  'person': '#f093fb',
};

const METAPHOR_LABELS = {
  'extension-of-self': 'Extension of Self',
  'magical-tool': 'Magical Tool',
  'person': 'Person',
};

export default function MetaphorNetwork({ data }) {
  const svgRef = useRef(null);

  useEffect(() => {
    if (!svgRef.current) return;

    const width = 900;
    const height = 400;

    // Clear previous
    d3.select(svgRef.current).selectAll('*').remove();

    const svg = d3
      .select(svgRef.current)
      .attr('width', width)
      .attr('height', height);

    // Create nodes for the 3 metaphors with uniform size (160px diameter = 80px radius)
    const nodes = [
      { id: 'extension-of-self', label: METAPHOR_LABELS['extension-of-self'], x: 200, y: 200, radius: 80 },
      { id: 'magical-tool', label: METAPHOR_LABELS['magical-tool'], x: 700, y: 200, radius: 80 },
      { id: 'person', label: METAPHOR_LABELS['person'], x: 450, y: 100, radius: 80 },
    ];

    // Create links for overlaps
    const links = [
      { source: 0, target: 1, value: data.extTool, label: data.extTool },
      { source: 0, target: 2, value: data.extPerson, label: data.extPerson },
      { source: 1, target: 2, value: data.toolPerson, label: data.toolPerson },
    ];

    // Draw links
    svg
      .selectAll('line')
      .data(links)
      .enter()
      .append('line')
      .attr('x1', d => nodes[d.source].x)
      .attr('y1', d => nodes[d.source].y)
      .attr('x2', d => nodes[d.target].x)
      .attr('y2', d => nodes[d.target].y)
      .attr('stroke', '#ccc')
      .attr('stroke-width', d => Math.sqrt(d.value) * 2)
      .attr('opacity', 0.6);

    // Draw nodes
    svg
      .selectAll('circle')
      .data(nodes)
      .enter()
      .append('circle')
      .attr('cx', d => d.x)
      .attr('cy', d => d.y)
      .attr('r', d => d.radius)
      .attr('fill', d => COLORS[d.id])
      .attr('stroke', '#fff')
      .attr('stroke-width', 3);

    // Draw labels
    svg
      .selectAll('text')
      .data(nodes)
      .enter()
      .append('text')
      .attr('x', d => d.x)
      .attr('y', d => d.y)
      .attr('text-anchor', 'middle')
      .attr('dy', '0.3em')
      .attr('fill', '#fff')
      .attr('font-weight', 'bold')
      .attr('font-size', '14px')
      .text(d => d.label);

    // Add overlap labels on links
    svg
      .selectAll('.link-label')
      .data(links)
      .enter()
      .append('text')
      .attr('class', 'link-label')
      .attr('x', d => (nodes[d.source].x + nodes[d.target].x) / 2)
      .attr('y', d => (nodes[d.source].y + nodes[d.target].y) / 2 - 10)
      .attr('text-anchor', 'middle')
      .attr('font-size', '14px')
      .attr('font-weight', 'bold')
      .attr('fill', '#333')
      .text(d => d.label);
  }, [data]);

  return (
    <div className="metaphor-network-container">
      <h2>Metaphor Relationships Network</h2>
      <p>Edge thickness represents number of products with overlapping metaphors</p>
      <svg ref={svgRef} className="metaphor-network-svg"></svg>
      <div className="overlap-details">
        <div className="overlap-item">
          <span className="overlap-label">Extension + Magical Tool:</span>
          <span className="overlap-count">{data.extTool} products</span>
        </div>
        <div className="overlap-item">
          <span className="overlap-label">Extension + Person:</span>
          <span className="overlap-count">{data.extPerson} products</span>
        </div>
        <div className="overlap-item">
          <span className="overlap-label">Magical Tool + Person:</span>
          <span className="overlap-count">{data.toolPerson} products</span>
        </div>
        <div className="overlap-item highlight">
          <span className="overlap-label">All Three Metaphors:</span>
          <span className="overlap-count">{data.all} products</span>
        </div>
      </div>
    </div>
  );
}
